/**
 * Панель стикеров.
 *
 * Стикер отправляется ПО ИДЕНТИФИКАТОРУ ФАЙЛА, а не загрузкой: он уже лежит
 * на серверах Telegram. Выгружать его туда повторно значило бы гонять
 * мегабайты ради того, что и так есть, и получить на выходе новый файл вместо
 * стикера из набора.
 *
 * Наборы грузятся один раз при первом открытии и держатся до размонтирования:
 * их немного, они не меняются в течение смены, а перезапрос при каждом
 * открытии давал бы пустую панель на секунду.
 *
 * Анимированные стикеры показываются первым кадром. Проигрывать их нечем:
 * формат tgs требует Lottie, а это отдельная зависимость ради украшения.
 */

import { useEffect, useRef, useState } from 'react';
import { useSession } from '../state/session.tsx';
import type { StickerSetDto } from '../lib/api.js';
import { StickerIcon } from './icons.tsx';
import { Button } from '@/components/ui/button';

interface StickerPickerProps {
  chatId: string;
  disabled: boolean;
  onPick: (fileId: string) => void | Promise<void>;
}

export function StickerPicker({ chatId, disabled, onPick }: StickerPickerProps) {
  const { api } = useSession();
  const [open, setOpen] = useState(false);
  const [sets, setSets] = useState<StickerSetDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const holder = useRef<HTMLDivElement | null>(null);

  // Закрытие по клику мимо панели: без этого она перекрывает ленту и
  // закрывается только повторным нажатием на кнопку.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent): void => {
      if (!holder.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open || sets !== null || loading) return;
    setLoading(true);
    api
      .stickerSets(chatId)
      .then(({ sets: loaded }) => setSets(loaded))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Не удалось загрузить стикеры'),
      )
      .finally(() => setLoading(false));
  }, [api, chatId, open, sets, loading]);

  return (
    <div ref={holder} className="relative flex-none">
      <Button
        variant="ghost"
        className="size-[42px] flex-none rounded-full text-muted-foreground hover:text-brand"
        disabled={disabled}
        aria-label="Стикеры"
        aria-expanded={open}
        title="Стикеры"
        onClick={() => setOpen((v) => !v)}
      >
        <StickerIcon className="size-[18px]" />
      </Button>

      {open && (
        <div className="absolute bottom-[calc(100%+10px)] left-0 z-20 max-h-[340px] w-[340px] overflow-y-auto rounded-2xl border border-border bg-popover p-3 shadow-xl">
          {loading && <p className="p-2 text-sm text-muted-foreground">Загружаем наборы…</p>}

          {error && (
            <p role="alert" className="p-2 text-sm text-destructive">
              {error}
            </p>
          )}

          {sets !== null && sets.length === 0 && (
            <p className="p-2 text-sm leading-relaxed text-muted-foreground">
              У аккаунта нет установленных наборов. Стикеры добавляются в самом
              Telegram — здесь показываются уже установленные.
            </p>
          )}

          {(sets ?? []).map((set) => (
            <section key={set.id} className="mb-3 last:mb-0">
              <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {set.title}
              </h4>
              <div className="grid grid-cols-5 gap-1">
                {set.stickers.map((sticker) => (
                  <button
                    key={sticker.fileId}
                    type="button"
                    className="grid aspect-square place-items-center rounded-lg border-transparent bg-transparent text-2xl transition-colors hover:bg-accent"
                    title={sticker.emoji ?? 'Стикер'}
                    onClick={() => {
                      void onPick(sticker.fileId);
                      setOpen(false);
                    }}
                  >
                    {/*
                      Показываем эмодзи стикера, а не его картинку.
                      Картинку пришлось бы тянуть отдельным запросом на каждый
                      стикер — это сотни запросов при открытии панели. Эмодзи
                      приходит вместе с набором и опознаётся не хуже.
                    */}
                    <span aria-hidden>{sticker.emoji ?? '🙂'}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
